/**
 * Tipos e interfaces para GeocodingOrchestrator
 * 
 * Extraído de GeocodingOrchestrator.ts como parte de TD-002.F
 * para modularización y mejor mantenibilidad.
 * 
 * @module services/geocoding/orchestrator/types
 * @version 1.0.0
 */

import type { 
  GeocodingResult,
  ClassificationResult,
} from '../../../types/infrastructure';
import type { GeocodabilityCategory } from '../orchestrator/AddressClassifier';
import type { AddressType } from '../../../types/processing';
import { InfrastructureType } from '../../../types/infrastructure';
import type { ScoreResult } from '../../../types/scoring';
import type { CrossValidationResult as CrossValidatorResult } from '../CrossValidator';
import type { CrossValidationResult as LibCrossValidationResult } from '../../../lib/crossValidation/types';

// ============================================================================
// CONSTANTES
// ============================================================================

/**
 * Mapa de fallbacks tipológicos para geocodificadores WFS.
 * 
 * Cuando el geocodificador primario no encuentra resultado,
 * se intentan estos geocodificadores alternativos antes de
 * saltar a los genéricos (CDAU, CartoCiudad).
 * 
 * Rationale:
 * - HEALTH → MUNICIPAL: Consultorios en edificios municipales
 * - EDUCATION → MUNICIPAL: Guarderías municipales, aulas
 * - SPORTS → MUNICIPAL, CULTURAL: Polideportivos municipales
 * - SECURITY → MUNICIPAL: Policía Local en ayuntamientos
 * - CULTURAL → MUNICIPAL, EDUCATION: Bibliotecas municipales
 * - EMERGENCY → SECURITY, MUNICIPAL: Protección civil
 * 
 * @since FUT-02 Tarea 4
 */
export const TYPOLOGY_FALLBACKS: Partial<Record<InfrastructureType, InfrastructureType[]>> = {
  [InfrastructureType.HEALTH]: [InfrastructureType.MUNICIPAL],
  [InfrastructureType.EDUCATION]: [InfrastructureType.MUNICIPAL],
  [InfrastructureType.SPORTS]: [InfrastructureType.MUNICIPAL, InfrastructureType.CULTURAL],
  [InfrastructureType.CULTURAL]: [InfrastructureType.MUNICIPAL, InfrastructureType.EDUCATION],
  [InfrastructureType.EMERGENCY]: [InfrastructureType.POLICE, InfrastructureType.MUNICIPAL],
  [InfrastructureType.FIRE]: [InfrastructureType.POLICE, InfrastructureType.MUNICIPAL],
  [InfrastructureType.POLICE]: [InfrastructureType.MUNICIPAL],
  [InfrastructureType.HYDRAULIC]: [InfrastructureType.MUNICIPAL, InfrastructureType.ENERGY],
  // L3-2: SOCIAL amplía fallbacks — EDUCATION y SPORTS cubren albergues en
  // centros educativos y pabellones deportivos; MUNICIPAL es el comodín final.
  [InfrastructureType.SOCIAL]: [
    InfrastructureType.EDUCATION,
    InfrastructureType.SPORTS,
    InfrastructureType.CULTURAL,
    InfrastructureType.MUNICIPAL,
  ],
};

/** Penalización de confianza para resultados de fallback tipológico */
export const FALLBACK_CONFIDENCE_PENALTY = 10;

/** Umbral mínimo de confianza para infras L3-B (sin dirección ni coords).
 *  Más permisivo que el general (60) porque solo se dispone del nombre. */
export const L3B_MIN_CONFIDENCE = 50;

// ============================================================================
// INTERFACES
// ============================================================================

/**
 * Opciones para geocodificación orquestada
 */
export interface OrchestrationOptions {
  /** Nombre de infraestructura */
  name: string;
  
  /** Municipio (CRÍTICO para filtrado espacial) */
  municipality: string;
  
  /** Provincia */
  province: string;
  
  /** Código INE del municipio (5 dígitos) - mejora precisión */
  codMun?: string;
  
  /** Dirección postal (para fallbacks genéricos) */
  address?: string;
  
  /** Tipología declarada en documento (hint para clasificador) */
  declaredType?: string;
  
  /** Tipo forzado (omitir clasificación automática) */
  forceType?: InfrastructureType;
  
  /** Usar datos locales DERA primero (L0 offline) - default: true */
  useLocalData?: boolean;
  
  /** Usar fallback genérico si falla especializado */
  useGenericFallback?: boolean;
  
  /** Usar NGA para topónimos */
  useNGA?: boolean;
  
  /** Usar IAID para deportes */
  useIAID?: boolean;
  
  /** Usar Overpass/OSM */
  useOverpass?: boolean;
  
  /** Timeout total en ms */
  timeout?: number;
  
  /** 
   * VALIDACIÓN CRUZADA MULTI-FUENTE (v3.0)
   * Consulta múltiples fuentes y calcula score compuesto
   * Score objetivo: 92-98% con detección errores ~95%
   * Default: true (siempre validar)
   */
  crossValidate?: boolean;
  
  /** Mínimo de fuentes a consultar para validación (default: 2) */
  minValidationSources?: number;

  /**
   * IEP-06: Tipo de dirección detectado en el documento fuente.
   * Cuando está presente, el orquestador ajusta la estrategia de
   * geocodificación (geocodificador prioritario, flags activados).
   *
   * | addressType | Efecto                                               |
   * |-------------|------------------------------------------------------|
   * | toponym     | Eleva NGA a oleada especulativa (si useNGA !== false) |
   * | cadastral   | Activa CatastroGeocoderV2 directamente               |
   * | road        | Propaga hint road a CartoCiudad                      |
   * | sports      | Fuerza useIAID: true                                 |
   * | postal      | Sin cambio (flujo normal)                            |
   * | telecom     | Sin cambio (rama telecom ya especializada)           |
   * | unknown     | Sin cambio (flujo normal)                            |
   */
  addressType?: AddressType;

  /**
   * CATPP-BC (parte C): número de polígono catastral extraído por el parser
   * (IEP-08, `ProcessedInfrastructure.poligonoCatastral`). Se reenvía a
   * `CatastroR2SearchOptions.polygon` para activar la estrategia
   * `polygon_parcel` en CatastroGeocoderV2 (rústicas «Pol X, Parc Y»).
   */
  poligonoCatastral?: string;

  /**
   * CATPP-BC (parte C): número de parcela catastral extraído por el parser
   * (IEP-08, `ProcessedInfrastructure.parcelaCatastral`). Se reenvía a
   * `CatastroR2SearchOptions.parcel`.
   */
  parcelaCatastral?: string;

  /**
   * CENTOR (12-sep-2026): el documento ya trae coordenada X/Y para este
   * elemento. Con `true`, ningún productor de centroide municipal actúa
   * (M3 universal, `specialized:social:centroide`, Fix-B, Issue #14-B,
   * `telecom:centroid_fallback`): un centroide no valida ni corrige una
   * coordenada del documento y solo costaba una llamada a INER2 y una
   * línea en `attempts`. Los `pre_centroide` de Catastro/CDAU no cambian.
   * Cierra `OBS-GEO-M3-CENTROIDE-IGNORA-COORD-DOCUMENTO`.
   */
  hasDocumentCoords?: boolean;
}

/**
 * Resultado de geocodificación orquestada
 */
export interface OrchestrationResult {
  /** Resultado de geocodificación (null si falla todo) */
  geocoding: GeocodingResult | null;
  
  /** Clasificación tipológica aplicada */
  classification: ClassificationResult;
  
  /** Geocodificador usado ('specialized:health' | 'nga' | 'iaid' | 'overpass' | 'cdau' | 'cartociudad' | 'none') */
  geocoderUsed: string;
  
  /** Tiempo total de procesamiento en ms */
  processingTime: number;
  
  /** Errores encontrados durante proceso */
  errors: string[];
  
  /** Intentos realizados */
  attempts: string[];
  
  /** Servicios saltados por circuit breaker (OPEN) */
  servicesSkipped?: string[];
  
  // ========== VALIDACIÓN CRUZADA (v3.0) ==========
  
  /** Score compuesto de validación cruzada (0-100) */
  crossValidationScore?: number;
  
  /** Estado de validación: CONFIRMED | LIKELY_VALID | UNCERTAIN | CONFLICT | SINGLE_SOURCE */
  validationStatus?: string;
  
  /** Discrepancia entre fuentes en metros */
  discrepancyMeters?: number | null;
  
  /** Requiere revisión manual */
  requiresManualReview?: boolean;
  
  /** Razón de revisión manual */
  reviewReason?: string;
  
  /** Desglose del score */
  scoreBreakdown?: {
    C_match: number;
    C_concordance: number;
    C_source: number;
    bonusApplied: number;
  };
  
  /** Resultado completo de validación cruzada (CrossValidator.validateEnhanced) */
  crossValidationDetail?: CrossValidatorResult;
  
  /**
   * F4.01: Resultado del motor de validación cruzada multi-fuente (lib/crossValidation).
   * Se invoca post-geocodificación en finalizeResult() cuando hay codMun.
   * Usa performCrossValidation() con clusters Huber, scoring compuesto y recomendación.
   */
  libCrossValidation?: LibCrossValidationResult;
  
  /**
   * DISAMV-REFINE-01 M5: Nivel de confianza de la desambiguación multi-campo.
   * Solo presente cuando geocoderUsed incluye 'disambiguated'.
   * - HIGH  → gap ≥ 15 pts → early return, conf=90, sin cap
   * - MEDIUM → gap ≥ 8 pts → cap confidence ≤ 55, elegible WFS rescue
   * - LOW   → gap < 8 pts  → cap confidence ≤ 55, elegible WFS rescue
   * @since DISAMV-REFINE-01
   */
  disambiguationConfidence?: 'HIGH' | 'MEDIUM' | 'LOW';

  /** Resultados de cada fuente consultada */
  sourcesConsulted?: Array<{
    source: string;
    x: number;
    y: number;
    confidence: number;
    responseTimeMs: number;
  }>;
  
  // ========== F025 ADDRESS EXTRACTION ==========

  // ========== F3.07 GEOCODABILITY CLASSIFICATION ==========
  
  /** Categoría de geocodificabilidad pre-cascada */
  geocodabilityCategory?: GeocodabilityCategory;
  
  /** Motivo legible de la clasificación (apto para exportación) */
  geocodabilityReason?: string;

  /** Nombre original de la infraestructura tal como viene en el documento (PROP-C fix Opción B) */
  originalName?: string;

  /** Resultado de extracción de dirección F025 */
  addressExtraction?: {
    /** Dirección original antes de normalizar */
    originalAddress: string;
    /** Dirección normalizada (null si no geocodificable) */
    normalizedAddress: string | null;
    /** Confianza de la dirección extraída (0-100) */
    confidence: number;
    /** Transformaciones aplicadas (debug) */
    transformations: string[];
  };

  // ========== TRF-06 VALIDACIÓN ESPACIAL ==========

  /**
   * CB-B TRUNC-SPATIAL: Indica que la coordenada Y del resultado fue corregida
   * por el detector de truncación (6 dígitos → 7 dígitos con prefijo 40/41/42).
   * Cuando es `true`, `finalizeResult()` invoca `TruncSpatialValidator` para
   * confirmar que la candidata seleccionada cae dentro del polígono municipal.
   *
   * Se propaga desde el normalizador de coordenadas a través del pipeline:
   *   CoordinateNormalizer → GeocodingOrchestrator (options) → OrchestrationResult
   */
  truncatedY?: boolean;

  /** Resultado de validación espacial (punto dentro del municipio) */
  spatialValidation?: SpatialValidationResult;

  // ========== SCORING V2 ==========
  
  /** Scoring v2 descompuesto (D1 Origen + D2 Precisión + D3 Validación + flags) */
  scoreDetail?: ScoreResult;

  // ========== CENTROIDE FALLBACK (Issue #14 A.NEW) ==========

  /**
   * Sugerencia de centroide municipal cuando ninguna fuente externa confirma
   * la coordenada (sourcesConfirming === 0, flag SIN_VALIDACION_EXTERNA).
   *
   * Caso real: Taller de Luis, Íllar (Almería) — error doc 145,2 m,
   * centroide municipal a 25,4 m del punto real.
   *
   * El usuario puede aceptar estas coordenadas pulsando «Usar centroide».
   */
  centroidSuggestion?: {
    /** Coordenada X centroide municipal (EPSG:25830) */
    x: number;
    /** Coordenada Y centroide municipal (EPSG:25830) */
    y: number;
    /** Código INE del municipio */
    codMun: string;
    /** Nombre del municipio */
    municipio: string;
  };
}

// ============================================================================
// TIPOS AUXILIARES
// ============================================================================

/**
 * Resultado de fuente consultada en validación cruzada
 */
export interface SourceConsultedResult {
  source: string;
  x: number;
  y: number;
  confidence: number;
  responseTimeMs: number;
}

/**
 * Desglose de score de validación cruzada
 */
export interface ScoreBreakdown {
  C_match: number;
  C_concordance: number;
  C_source: number;
  bonusApplied: number;
}

/**
 * Resultado de validación espacial TRF-06 + PostGIS (T2)
 */
export interface SpatialValidationResult {
  isValid: boolean | undefined;
  message: string;
  wgs84Coords?: { lat: number; lng: number };
  
  /** T2 Fase 0: Validación PostGIS con polígonos municipales reales */
  postgis?: {
    /** PostGIS está disponible y configurado */
    available: boolean;
    /** Coordenada dentro del municipio según ST_Contains */
    isWithinMunicipality: boolean;
    /** Código INE del municipio donde está realmente */
    actualMunicipalityCode: string | null;
    /** Nombre del municipio donde está realmente */
    actualMunicipalityName: string | null;
    /** Distancia al centroide del municipio esperado (metros) */
    distanceToCentroid: number | null;
    /** Distancia al límite más cercano (metros) */
    distanceToNearestBoundary: number | null;
    /** ¿Está en un municipio adyacente? */
    isInAdjacentMunicipality: boolean;
    /** Latencia de la consulta PostGIS (ms) */
    queryLatency: number;
  };
}

/**
 * Resultado de extracción de dirección F025
 */
export interface AddressExtractionResult {
  originalAddress: string;
  normalizedAddress: string | null;
  confidence: number;
  transformations: string[];
}

// ============================================================================
// F9.01a-fix: CIRCUIT BREAKER — RESILIENT FAILOVER
// ============================================================================

/** Servicios online que pueden degradarse durante un lote */
export type OnlineServiceId = 'cdau' | 'cartociudad' | 'nominatim';

/** Umbral de fallos consecutivos antes de marcar degradado */
export const CIRCUIT_BREAKER_FAILURE_THRESHOLD = 2;

/** Tiempo de cooldown (ms) antes de reintentar un servicio degradado: 5 min */
export const CIRCUIT_BREAKER_COOLDOWN_MS = 5 * 60 * 1000;

/**
 * Estado de salud de un servicio online individual.
 * El circuit breaker desactiva servicios con fallos repetidos
 * para evitar timeouts en cascada que consumen presupuesto.
 */
export interface ServiceHealthState {
  /** Número de fallos consecutivos (se resetea con éxito) */
  consecutiveFailures: number;
  /** ¿Servicio degradado? (skip automático) */
  isDegraded: boolean;
  /** Timestamp del último fallo (para cooldown) */
  lastFailureTime: number;
  /** Total de intentos en el lote actual */
  totalAttempts: number;
  /** Total de éxitos en el lote actual */
  totalSuccesses: number;
  /** Infraestructuras que se saltaron por degradación */
  skippedCount: number;
}
